import { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { translations, languages } from "./translations";

const LanguageContext = createContext({ lang: "en", t: translations.en, switchLang: () => {} });

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Detect language from URL prefix
  const lang = location.pathname.startsWith("/es") ? "es" : "en";
  const t = translations[lang];

  // Update <html lang> + document title + meta description + OG/Twitter for SEO
  useEffect(() => {
    document.documentElement.lang = languages[lang].htmlLang;
    document.title = t.meta.title;

    const setMeta = (kind, key, value) => {
      // kind: "name" or "property"
      let el = document.querySelector(`meta[${kind}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(kind, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    setMeta("name", "description", t.meta.description);
    setMeta("property", "og:title", t.meta.title);
    setMeta("property", "og:description", t.meta.description);
    setMeta("property", "og:locale", lang === "es" ? "es_419" : "en_US");
    setMeta("property", "og:locale:alternate", lang === "es" ? "en_US" : "es_419");
    setMeta("name", "twitter:title", t.meta.title);
    setMeta("name", "twitter:description", t.meta.description);

    const origin = window.location.origin;
    const hashAndSearch = window.location.search + window.location.hash;
    const currentUrl = lang === "es" ? `${origin}/es${hashAndSearch}` : `${origin}/${hashAndSearch}`;
    setMeta("property", "og:url", currentUrl);

    // canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = currentUrl;

    // hreflang alternates for Google
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
    const addAlt = (hl, href) => {
      const l = document.createElement("link");
      l.rel = "alternate";
      l.hreflang = hl;
      l.href = href;
      document.head.appendChild(l);
    };
    addAlt("en", `${origin}/${hashAndSearch}`);
    addAlt("es", `${origin}/es${hashAndSearch}`);
    addAlt("x-default", `${origin}/${hashAndSearch}`);
  }, [lang, t]);

  // Auto-detect on first visit (only once, only when visiting root)
  useEffect(() => {
    if (location.pathname !== "/") return;
    try {
      const already = localStorage.getItem("fwdco-lang-choice");
      if (already) return;
      const browser = (navigator.language || navigator.userLanguage || "").toLowerCase();
      if (browser.startsWith("es")) {
        localStorage.setItem("fwdco-lang-choice", "es");
        navigate("/es", { replace: true });
      }
    } catch (_) {
      // localStorage may be unavailable (SSR / private mode) — silently skip
    }
  }, [location.pathname, navigate]);

  const switchLang = (target) => {
    try { localStorage.setItem("fwdco-lang-choice", target); } catch (_) { /* ignore */ }
    const hash = window.location.hash || "";
    if (target === "es") navigate("/es" + hash);
    else navigate("/" + hash);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo(() => ({ lang, t, switchLang }), [lang, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useTranslation() {
  return useContext(LanguageContext);
}
